import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {OrgDto} from "../../dto/org.dto";
import {SearchRequest} from "../../dto/search/search-table.dto.req";
import {UrlBuilder} from "../../item/url.item";
import {WrapperDto} from "../../dto/wrapper.dto";

import {Observable, tap} from "rxjs";
import {map} from "rxjs/operators";
import {VehicleDto} from "../../dto/vehicle/vehicle.dto";
import {VoidDto} from "../../dto/void.dto";
import {SearchVehicleRes} from "../../dto/vehicle/search-vehicle.res";
import {MediaDto} from "../../dto/media.dto";

@Injectable({providedIn: 'root'})
export class ContactVehicleRepo {

  private readonly cvSearch = '/v1/org/{orgId}/contact/{contactId}/vehicle/search';
  private readonly cvRead = '/v1/org/{orgId}/contact/{contactId}/vehicle/{id}';
  private readonly cvWrite = '/v1/org/{orgId}/contact/{contactId}/vehicle';
  private readonly cvDelete = '/v1/org/{orgId}/contact/{contactId}/vehicle/{id}/delete';
  private readonly cvTireChangeSuccess = '/v1/org/{orgId}/contact/{contactId}/vehicle/{id}/tire/change/success';
  private readonly cvTireChangeClear = '/v1/org/{orgId}/contact/{contactId}/vehicle/{id}/tire/change/clear';
  private readonly cvPrintoutWrite = '/v1/org/{orgId}/contact/{contactId}/vehicle/{id}/printout';

  private readonly http: HttpClient = inject(HttpClient);

  public getById(org: OrgDto, contactId: string, vehicleId: string): Observable<WrapperDto<VehicleDto>> {
    const url = UrlBuilder.builder().addPath(this.cvRead).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('id', vehicleId).build();
    return this.http.get<WrapperDto<VehicleDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public delete(org: OrgDto, contactId: string, vehicleId: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.cvDelete).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('id', vehicleId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto, contactId: string, vehicle: VehicleDto): Observable<WrapperDto<VehicleDto>> {
    const url = UrlBuilder.builder().addPath(this.cvWrite).addParam('orgId', org.id)
      .addParam('contactId', contactId).build();
    return this.http.post<WrapperDto<VehicleDto>>(url.getUrl(), vehicle, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public tireChangeSuccess(org: OrgDto, contactId: string, id: string): Observable<WrapperDto<VehicleDto>> {
    const url = UrlBuilder.builder().addPath(this.cvTireChangeSuccess).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('id', id).build();
    return this.http.post<WrapperDto<VehicleDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public tireChangeClear(org: OrgDto, contactId: string, id: string): Observable<WrapperDto<VehicleDto>> {
    const url = UrlBuilder.builder().addPath(this.cvTireChangeClear).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('id', id).build();
    return this.http.post<WrapperDto<VehicleDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createPrintout(org: OrgDto, contactId: string, id: string): Observable<WrapperDto<MediaDto>> {
    const url = UrlBuilder.builder().addPath(this.cvPrintoutWrite).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('id', id).build();
    return this.http.post<WrapperDto<MediaDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchVehicleRes>> {
    const url = UrlBuilder.builder().addPath(this.cvSearch).addParam('orgId', org.id).addParam('contactId', 'all').build();
    return this.http.post<WrapperDto<SearchVehicleRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchVehicleRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }
}
